import { roundToNearest, USER_ROUNDING_NUM_DECIMALS } from '@common/neo.tax';
import { Loading, useModal } from '@ui-components/neo.tax';
import { ReactElement, useMemo } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import { useRdExpense, useRdExpenseProjectAllocations } from '@neo.tax/rd/hooks';
import { RdCategory } from '@neo.tax/shared';

import { EditQreAllocation } from './EditQreAllocation';
import { EditQreExpenseAllocationModalLoader } from './EditQreExpenseAllocationModalLoader';

type ContributorQreAllocationProps = {
  rdExpenseId: string;
};

export const EditQreExpenseAllocations = ({ rdExpenseId }: ContributorQreAllocationProps): ReactElement => {
  const { fiscalYearId } = useRdContext();
  const { loading: loadingRdExpense, rdExpense } = useRdExpense(rdExpenseId);
  const { loading: loadingRdExpenseProjectAllocations, rdExpenseProjectAllocations } = useRdExpenseProjectAllocations({
    rdExpenseId,
    fiscalYearId,
  });

  const { isOpen, setIsOpen } = useModal();

  const { projectAllocations, totalQualifiedAllocationPercent } = useMemo(() => {
    if (loadingRdExpense || loadingRdExpenseProjectAllocations || !rdExpense || !rdExpenseProjectAllocations) {
      return { projectAllocations: [], totalQualifiedAllocationPercent: 0 };
    }

    const projectPerExpenseWithAllocations = rdExpenseProjectAllocations.filter(
      (projectPerExpense) => projectPerExpense.allocationPercentNotRounded > 0,
    );

    const projectAllocations = projectPerExpenseWithAllocations
      .sort((a, b) => b.allocationPercentNotRounded - a.allocationPercentNotRounded)
      .map((projectPerPe) => ({
        projectId: projectPerPe.rdProjectId,
        allocationPercent: roundToNearest(projectPerPe.allocationPercentNotRounded, USER_ROUNDING_NUM_DECIMALS),
      }));

    const _totalQualifiedAllocationPercent = projectPerExpenseWithAllocations
      .filter(
        (projectPerExpense) =>
          projectPerExpense.project.consideredForRd &&
          ![RdCategory.NonRd, RdCategory.Overhead].includes(rdExpense.rdCategory),
      )
      .reduce((acc, projectPerPe) => acc + projectPerPe.allocationPercentNotRounded, 0);
    const totalQualifiedAllocationPercent = roundToNearest(
      _totalQualifiedAllocationPercent,
      USER_ROUNDING_NUM_DECIMALS,
    );

    return { projectAllocations, totalQualifiedAllocationPercent };
  }, [loadingRdExpense, rdExpense, loadingRdExpenseProjectAllocations, rdExpenseProjectAllocations]);

  if (loadingRdExpense || loadingRdExpenseProjectAllocations || !rdExpense) {
    return <Loading height={120} />;
  }

  return (
    <>
      <EditQreAllocation
        projectAllocations={projectAllocations}
        totalRdProjectAllocation={totalQualifiedAllocationPercent}
        onEditClick={(): void => setIsOpen(true)}
      />
      <EditQreExpenseAllocationModalLoader
        isOpen={isOpen}
        close={(): void => setIsOpen(false)}
        rdExpenseId={rdExpense.id}
      />
    </>
  );
};
